import {
  BankVerificationRequestV1,
  EmailDomainVerificationRequestV1,
  EuVatVerificationRequestV1,
  GstVerificationRequestV1,
  IbanVerificationRequestV1,
  TinVerificationRequestV1,
} from 'src/app/api/models';
import { resolveField, type VerificationFieldConfig } from '../types/types';
import { DetectedField } from './verification-field-detector';

function safeStr(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

function r(fieldData: Record<string, unknown>, names: string[]): unknown {
  return resolveField(fieldData, names);
}

export function buildBankBody(
  requestId: string,
  field: DetectedField,
  fieldConfig: VerificationFieldConfig[],
  isBusiness?: boolean,
  forceRefresh = false
): BankVerificationRequestV1 {
  const { fieldData } = field;
  const bf = fieldConfig.find((c) => c.type.toUpperCase() === 'BANK')?.fields ?? {};
  const holder = bf['holderName'] ?? [];
  const number = bf['accountNumber'] ?? [];
  const clearing = bf['clearingSystemId'] ?? [];
  const creditor = bf['creditorIdentifier'] ?? [];
  const creditorType = bf['creditorIdentifierType'] ?? [];
  const clearingVal = r(fieldData, clearing);
  const creditorVal = r(fieldData, creditor);
  const creditorTypeVal = r(fieldData, creditorType);
  return {
    requestId,
    bankAccountHolderName: safeStr(r(fieldData, holder)).trim(),
    bankAccountNumber: safeStr(r(fieldData, number)).trim(),
    bankAccountClearingSystemId: clearingVal == null ? undefined : safeStr(clearingVal),
    bankAccountCreditorIdentifier: creditorVal == null ? undefined : safeStr(creditorVal),
    bankAccountCreditorIdentifierType: creditorTypeVal == null ? undefined : safeStr(creditorTypeVal),
    isBusiness,
    forceRefresh,
  };
}

export function buildTinBody(
  requestId: string,
  field: DetectedField,
  fieldConfig: VerificationFieldConfig[],
  forceRefresh = false
): TinVerificationRequestV1 {
  const { fieldData } = field;
  const tf = fieldConfig.find((c) => c.type.toUpperCase() === 'TIN')?.fields ?? {};
  return {
    requestId,
    tin: safeStr(r(fieldData, tf['tin'] ?? [])).replaceAll(/\s+/g, ''),
    name: safeStr(r(fieldData, tf['name'] ?? []))
      .replaceAll(/\s+/g, ' ')
      .trim(),
    forceRefresh,
  };
}

export function buildGstBody(
  requestId: string,
  field: DetectedField,
  fieldConfig: VerificationFieldConfig[],
  forceRefresh = false
): GstVerificationRequestV1 {
  const { fieldData } = field;
  const gf = fieldConfig.find((c) => c.type.toUpperCase() === 'GST')?.fields ?? {};
  return {
    requestId,
    gstNumber: safeStr(r(fieldData, gf['gstNumber'] ?? [])).replaceAll(/\s+/g, ''),
    businessName: safeStr(r(fieldData, gf['businessName'] ?? []))
      .replaceAll(/\s+/g, ' ')
      .trim(),
    transactionDate: safeStr(r(fieldData, gf['transactionDate'] ?? [])).trim(),
    forceRefresh,
  };
}

export function buildEuVatBody(
  requestId: string,
  field: DetectedField,
  fieldConfig: VerificationFieldConfig[],
  forceRefresh = false
): EuVatVerificationRequestV1 {
  const { fieldData } = field;
  const vf = fieldConfig.find((c) => c.type.toUpperCase() === 'EU_VAT')?.fields ?? {};
  return {
    requestId,
    vatNumber: safeStr(r(fieldData, vf['vatNumber'] ?? [])).replaceAll(/\s+/g, ''),
    forceRefresh,
  };
}

export function buildIbanBody(
  requestId: string,
  field: DetectedField,
  fieldConfig: VerificationFieldConfig[],
  forceRefresh = false
): IbanVerificationRequestV1 {
  const { fieldData } = field;
  const ibf = fieldConfig.find((c) => c.type.toUpperCase() === 'IBAN');
  const anchorNames = ibf ? ibf.fields[ibf.anchorField] ?? [] : [];
  return {
    requestId,
    ibanNumber: safeStr(r(fieldData, anchorNames)).replaceAll(/\s+/g, ''),
    forceRefresh,
  };
}

export function buildEmailBody(
  requestId: string,
  field: DetectedField,
  forceRefresh = false
): EmailDomainVerificationRequestV1 {
  return {
    requestId,
    emailAddress: safeStr(field.fieldData['emailAddress']).trim(),
    forceRefresh,
  };
}
